/**
 * Lê parâmetros de query de forma case-insensitive (ex.: UTM_SOURCE vs utm_source).
 * Usado para attribution: plataformas e templates variam o casing.
 */
export function getSearchParamInsensitive(searchParams: URLSearchParams, name: string): string | null {
  const target = name.toLowerCase()
  for (const [key, value] of searchParams) {
    if (key.toLowerCase() === target && value) {
      return value
    }
  }
  return null
}

export interface UtmClickBundle {
  source: string | null
  medium: string | null
  campaign: string | null
  content: string | null
  term: string | null
  gclid: string | null
  fbclid: string | null
  ttclid: string | null
  gbraid: string | null
  wbraid: string | null
}

export function extractUtmClickBundleFromUrl(url: URL): UtmClickBundle {
  const p = url.searchParams
  return {
    source: getSearchParamInsensitive(p, "utm_source"),
    medium: getSearchParamInsensitive(p, "utm_medium"),
    campaign: getSearchParamInsensitive(p, "utm_campaign"),
    content: getSearchParamInsensitive(p, "utm_content"),
    term: getSearchParamInsensitive(p, "utm_term"),
    gclid: getSearchParamInsensitive(p, "gclid"),
    fbclid: getSearchParamInsensitive(p, "fbclid"),
    ttclid: getSearchParamInsensitive(p, "ttclid"),
    gbraid: getSearchParamInsensitive(p, "gbraid"),
    wbraid: getSearchParamInsensitive(p, "wbraid"),
  }
}

function pickNonEmpty(a: string | null, b: string | null): string | null {
  const t = (a ?? "").trim()
  if (t) return a
  const u = (b ?? "").trim()
  return u ? b : null
}

/** Preenche campos vazios do bundle principal com valores do fallback (ex.: referrer). */
export function mergeUtmClickBundles(primary: UtmClickBundle, fallback: UtmClickBundle): UtmClickBundle {
  return {
    source: pickNonEmpty(primary.source, fallback.source),
    medium: pickNonEmpty(primary.medium, fallback.medium),
    campaign: pickNonEmpty(primary.campaign, fallback.campaign),
    content: pickNonEmpty(primary.content, fallback.content),
    term: pickNonEmpty(primary.term, fallback.term),
    gclid: pickNonEmpty(primary.gclid, fallback.gclid),
    fbclid: pickNonEmpty(primary.fbclid, fallback.fbclid),
    ttclid: pickNonEmpty(primary.ttclid, fallback.ttclid),
    gbraid: pickNonEmpty(primary.gbraid, fallback.gbraid),
    wbraid: pickNonEmpty(primary.wbraid, fallback.wbraid),
  }
}

const MARKETING_QUERY_PREFIXES = ["utm_"]
const MARKETING_QUERY_KEYS = new Set(["gclid", "fbclid", "ttclid", "gbraid", "wbraid"])

function isMarketingQueryKey(key: string): boolean {
  const lk = key.toLowerCase()
  if (MARKETING_QUERY_KEYS.has(lk)) return true
  return MARKETING_QUERY_PREFIXES.some((p) => lk.startsWith(p))
}

function rawQueryKeysLower(raw: Record<string, string | string[]>): Set<string> {
  return new Set(Object.keys(raw).map((k) => k.toLowerCase()))
}

/**
 * Acrescenta ao raw_query parâmetros de marketing da URL secundária que ainda não existem (case-insensitive).
 */
export function mergeMarketingParamsIntoRawQuery(
  rawQuery: Record<string, string | string[]>,
  secondary: URL,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = { ...rawQuery }
  const existing = rawQueryKeysLower(out)

  secondary.searchParams.forEach((value, key) => {
    if (!value) return
    const lk = key.toLowerCase()
    if (!isMarketingQueryKey(key)) return
    if (existing.has(lk)) return
    out[key] = value
    existing.add(lk)
  })

  return out
}
